"use client";
import { useState, useEffect } from "react";
import { Globe, Pencil } from "lucide-react";
import { useLocation } from "react-router-dom";
import AdminModal from "../modals/AdminModal";
import EditCompanyForm from "./EditCompanyForm";
import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

type Props = {
  name: string;
  phoneNumber: string;
  industry: string; // ← ID dari API (contoh: "3")
  size: string;     // ← ID dari API
  logo: string;
  website: string;
  end_date: string;
  slot_posting: string;
  package_name: string;
  package_id: string;
  start_date: string;
  lang?: any;
};

export default function AdminCompanyBanner({
  name,
  phoneNumber,
  industry,
  size,
  logo,
  website,
  end_date,
  slot_posting,
  package_name,
  package_id,
  start_date,
  lang,
}: Props) {
  const { packages, profile,
    fetchCompanyProfile, fetchCompanyPackage, updateCompanyDetails } = useCompanyProfileStore();
  const [companyForm, setCompanyForm] = useState<any>(null);
  const location = useLocation();

  const { industries, sizes, fetchFilters } = useJobFilterStore();

  useEffect(() => {
    fetchFilters(lang);
    fetchCompanyPackage();
  }, [lang, fetchFilters, fetchCompanyPackage]);


  const closeModal = () => setModalType(null);

  // ✅ mapping ID -> NAME
  const industryName =
    industries.find((i: any) => i.id === Number(industry))?.name || "-";

  const sizeName =
    sizes.find((s: any) => s.id === Number(size))?.name || "-";

  const packageName =
    packages.find((s: any) => s.id === Number(size))?.package_name || "-";

  // ====== CONTRACT CALCULATION ======
  const today = new Date();
  const endDate = new Date(end_date);

  const contractStart = start_date
    ? new Date(start_date)
    : new Date(endDate.getFullYear(), 0, 1);

  const msInDay = 1000 * 60 * 60 * 24;

  const totalContractDays = Math.max(
    0,
    Math.round((endDate.getTime() - contractStart.getTime()) / msInDay)
  );

  const daysPassed = Math.max(
    0,
    Math.round((today.getTime() - contractStart.getTime()) / msInDay)
  );

  const progress =
    totalContractDays > 0
      ? Math.min(100, Math.max(0, (daysPassed / totalContractDays) * 100))
      : 0;

  const remainingDays = Math.max(0, totalContractDays - daysPassed);

  const [modalType, setModalType] = useState<string | null>(null);

  return (
    <>
      <div className="w-full bg-white rounded-xl shadow p-5 relative">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Logo */}
          <div className="flex justify-center md:block">
            <img
              src={logo || "https://placehold.co/200"}
              className="w-20 h-20 rounded-lg object-cover"
            />
          </div>

          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl font-semibold">{name || "No company name yet"}</h2>

            <div className="text-gray-500 text-sm my-1 flex flex-col md:flex-row md:gap-4">
              <span>{industryName || "No industry yet"}</span>
              <span className="hidden md:block">•</span>
              <span>
                {sizeName || "No size name yet"}
              </span>
            </div>

            <a
              href={website}
              target="_blank"
              className="text-primary font-medium flex gap-2 justify-center mb-2 md:justify-start items-center"
            >
              <Globe size={18} />
              <span>{website || "No company website yet"}</span>
            </a>
            <span> Verification Status: </span>
            <span
            className={`px-2 py-1 rounded font-semibold text-white ${
              profile?.package_status === "approved"
                ? "bg-green-500"
                : profile?.package_status === "rejected"
                ? "bg-red-500"
                : profile?.package_status === "reviewed"
                ? "bg-yellow-500"
                : ""
            }`}
          >
           {profile?.package_status || "-"}
          </span>

          </div>

          {location.pathname.includes("/admin/company-profile") && (
            <button
              onClick={() => setModalType("edit_company")}
              className="flex items-center gap-1 text-primary"
            >
              <Pencil size={16} /> Edit
            </button>
          )}

          {/* Package Info */}
          <div className="p-4 bg-gray-50 rounded-xl shadow-sm border">
            <div className="flex gap-6">
              <div>
                <p className="text-sm text-gray-500">
                  {lang === "id" ? "Tanggal Kontrak Sampai" : "Contract Until"}
                </p>
                <p className="font-medium">{end_date}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">
                  {lang === "id" ? "Paket" : "Package"}
                </p>
                <p className="font-semibold">{package_name || "-"}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">
                  {lang === "id" ? "Kuota Postingan" : "Quota Posting"}
                </p>
                <p className="font-semibold">{slot_posting || "-"}</p>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div
                className="h-2 bg-primary rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p className="text-xs text-gray-500 mt-1">
              {remainingDays || "0"} {lang === "id" ? "hari tersisa" : "days remaining"}
            </p>
          </div>
        </div>
      </div>

      <AdminModal
        open={modalType === "edit_company"}
        onClose={closeModal}
        title="Edit Company Profile"
        isValid={companyForm?.isValid}
        onSubmit={async () => {
          if (!companyForm) return;

          const res = await updateCompanyDetails(companyForm);

          if (res.success) {
            toast.success(res.message);
            await fetchCompanyProfile(); // konsisten kayak Office
            closeModal();
          } else {
            toast.error(res.message);
          }
        }}
      >
        <EditCompanyForm
          initialData={{ name, phoneNumber, industry, size, website, logo }}
          onChange={setCompanyForm}
        />
      </AdminModal>

    </>
  );
}
